import { memo, useEffect, useRef, useState } from "react"
import { Stack, Text, Center, Button, Container, Box, Input, VStack, Card } from "@chakra-ui/react"
import { useCart } from "contexts/cart"
import { formatCurrency } from "@/utilities/number"
import { useApp } from "contexts/app"
import { useForm } from "react-hook-form"
import { Field } from "@/components/ui/field"
import { useNavigate } from "react-router"
import { useAuth } from "contexts/auth"
import { useStore } from "contexts/store"
import ProfileService from "services/profile"
import BottomSheet from "components/bottom-sheet"
import _ from "lodash"

const profileService = new ProfileService();

const GuestCheckout = () => {
    const { setProcessing, setPageTitle, showToast } = useApp();
    const { showLogin } = useAuth();
    const { store } = useStore();
    const { cart, updateShippingAddress, completeCart, create, validateInventory } = useCart();
    const formRef = useRef();
    const navigate = useNavigate();
    const invoiceEnabled = !!store?.order_setting?.invoice_enabled;

    useEffect(() => {
        setPageTitle("Thanh toán")
    }, [setPageTitle])

    const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm();
    const [showTaxForm, setShowTaxForm] = useState(false);

    const fillCustomerForm = (customer) => {
        if (!customer) return;
        const invoiceInfo = customer?.invoice_info || {};
        setValue("phone_number", customer?.phone || customer?.phone_number || "");
        setValue("full_name", customer?.full_name || "");
        setValue("address", customer?.address || "");
        setValue("invoice_company_name", invoiceInfo.company_name || "");
        setValue("invoice_tax_number", invoiceInfo.tax_number || "");
        setValue("invoice_address", invoiceInfo.address || "");
        setValue("invoice_email", invoiceInfo.email || "");
        setValue("invoice_note", invoiceInfo.note || "");
    };

    useEffect(() => {
        if (!_.isEmpty(cart?.shipping_address)) {
            fillCustomerForm(cart.shipping_address);
        }
    }, [cart?.id, invoiceEnabled]);

    const onCartComplete = async (data) => {
        setProcessing(true);
        try {
            const invoiceInfo = invoiceEnabled ? {
                company_name: data.invoice_company_name?.trim?.() || "",
                tax_number: data.invoice_tax_number?.trim?.() || "",
                address: data.invoice_address?.trim?.() || "",
                email: data.invoice_email?.trim?.() || "",
                note: data.invoice_note?.trim?.() || "",
                tax_percent: Number(store?.order_setting?.tax_percent ?? 0),
            } : null;

            const hasInvoiceInfo = invoiceInfo && Object.values(invoiceInfo).some((value) => !!String(value).trim());
            await updateShippingAddress({
                phone_number: data.phone_number,
                full_name: data.full_name,
                address: data.address,
                note: data.note,
                invoice_info: hasInvoiceInfo ? invoiceInfo : null,
            });

            if (hasInvoiceInfo && localStorage.getItem("token")) {
                const currentProfile = await profileService.get();
                await profileService.updateInfo({
                    full_name: data.full_name || currentProfile?.info?.full_name || "",
                    address: data.address || currentProfile?.info?.address || "",
                    invoice_info: invoiceInfo,
                });
            }

            await validateInventory();
            var order = await completeCart();
            await create();
            setProcessing(false);
            showToast({ description: "Vui lòng thanh toán để hoàn tất đơn hàng", type: "success" });
            navigate(`/order-pay/${order.id}`);
        } catch (error) {
            if (error?.error_code === "voucher_not_allow") {
                showToast({
                    description: "Không thể áp dụng mã khuyến mãi. Vui lòng xoá mã khuyến mãi để tiếp tục",
                    type: "error",
                    duration: 6000,
                });
                navigate("/cart");
                setProcessing(false);
                return;
            }

            if (Array.isArray(error?.inventory_errors) && error.inventory_errors.length > 0) {
                showToast({
                    description: "Một số sản phẩm không đủ tồn kho hoặc đã hết hàng. Vui lòng cập nhật số lượng hoặc xoá món đã hết hàng.",
                    type: "error",
                    duration: 6000,
                });
                navigate("/cart");
                setProcessing(false);
                return;
            }

            setProcessing(false);
        }
    };

    const onSubmit = handleSubmit(async (data) => {
        if (invoiceEnabled && showTaxForm) {
            const missingFields = [];
            if (!String(data.invoice_company_name || "").trim()) missingFields.push("Tên công ty");
            if (!String(data.invoice_tax_number || "").trim()) missingFields.push("Mã số thuế");
            if (!String(data.invoice_address || "").trim()) missingFields.push("Địa chỉ hoá đơn");
            if (!String(data.invoice_email || "").trim()) missingFields.push("Email");

            if (missingFields.length > 0) {
                showToast({
                    description: `Vui lòng điền: ${missingFields.join(", ")}`,
                    type: "error",
                    duration: 5000,
                });
                return;
            }
        }

        var loggedInUser = await showLogin({ phone: data.phone_number, customer_info: data });
        if (!loggedInUser) return;
        fillCustomerForm(loggedInUser);
        setTimeout(() => onCartComplete(data), 500);
    });

    const onExistingAccountLogin = async () => {
        var customerLogin = await showLogin({});
        if (!customerLogin) return;
        fillCustomerForm(customerLogin);
        await updateShippingAddress({
            phone_number: customerLogin?.phone || customerLogin?.phone_number,
            full_name: customerLogin?.full_name,
            address: customerLogin?.address,
        });
        navigate("/checkout", { replace: true });
    };

    return (
        <div style={{ paddingBottom: 75 }}>
            <form ref={formRef} onSubmit={onSubmit}>
                <Stack gap="4" align="flex-start" maxW="sm">
                    <Text>Thông tin khách hàng</Text>

                    <Field label="Số điện thoại" invalid={!!errors.phone_number} errorText={errors.phone_number?.message}>
                        <Input {...register("phone_number", { required: "Bạn phải nhập số điện thoại" })} />
                    </Field>
                    <Field label="Họ và tên" invalid={!!errors.full_name} errorText={errors.full_name?.message}>
                        <Input {...register("full_name", { required: "Bạn phải nhập họ và tên" })} />
                    </Field>
                    <Field label="Địa chỉ" invalid={!!errors.address} errorText={errors.address?.message}>
                        <Input {...register("address", { required: "Bạn phải nhập địa chỉ" })} />
                    </Field>
                    <Field label="Ghi chú">
                        <Input {...register("note")} placeholder="Ghi chú thêm cho đơn hàng" />
                    </Field>

                    {invoiceEnabled && (
                        <VStack style={{ width: "100%" }} align="stretch" gap={3}>
                            <Button type="button" variant="link" color="blue.600" onClick={() => setShowTaxForm(true)}>
                                {Object.values({
                                    company_name: watch?.("invoice_company_name") || "",
                                    tax_number: watch?.("invoice_tax_number") || "",
                                    address: watch?.("invoice_address") || "",
                                    email: watch?.("invoice_email") || "",
                                }).some((value) => String(value).trim()) ? "Sửa thông tin thuế" : "Thêm thông tin thuế"}
                            </Button>
                            <BottomSheet
                                open={showTaxForm}
                                onOpenChange={(open) => setShowTaxForm(open)}
                                title="Thông tin hoá đơn"
                            >
                                <Stack gap={3} pb={4}>
                                    <Field label="Tên công ty">
                                        <Input {...register("invoice_company_name")} placeholder="Tên công ty" />
                                    </Field>
                                    <Field label="Mã số thuế">
                                        <Input {...register("invoice_tax_number")} placeholder="Mã số thuế" />
                                    </Field>
                                    <Field label="Địa chỉ hoá đơn">
                                        <Input {...register("invoice_address")} placeholder="Địa chỉ hoá đơn" />
                                    </Field>
                                    <Field label="Email">
                                        <Input {...register("invoice_email")} placeholder="Email" />
                                    </Field>
                                    <Field label="Ghi chú hoá đơn">
                                        <Input {...register("invoice_note")} placeholder="Ghi chú cho hoá đơn" />
                                    </Field>
                                    <Button type="button" onClick={() => setShowTaxForm(false)}>Lưu thông tin thuế</Button>
                                </Stack>
                            </BottomSheet>
                        </VStack>
                    )}

                    <VStack style={{ width: "100%" }}>
                        <Button type="button" variant="outline" onClick={onExistingAccountLogin}>
                            Đăng nhập nếu bạn đã có tài khoản
                        </Button>
                    </VStack>
                </Stack>
            </form>

            <div style={{ position: "fixed", bottom: 0, left: 0, padding: 0, width: "100vw" }}>
                <Container maxW="md" style={{ padding: 0 }}>
                    <Box bg="bg" shadow="md">
                        <div style={{ display: "flex", padding: 16 }}>
                            <div style={{ flexGrow: 1 }}>
                                <Center height="100%">
                                    <Text fontWeight="semibold">{formatCurrency(cart?.total_amount)}</Text>
                                </Center>
                            </div>
                            <div>
                                <Button onClick={() => handleSubmit(onSubmit)()}>Hoàn tất</Button>
                            </div>
                        </div>
                    </Box>
                </Container>
            </div>
        </div>
    );
};

export default memo(GuestCheckout);
